import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import {
	BAR_VALUE_LABEL_RIGHT,
	NO_ANIMATION,
	WrappedCategoryTickY,
	chartHeightForRows,
	CATEGORY_Y_AXIS_WIDTH,
} from "./chart-ticks";
import { cn } from "@/lib/utils";
import { COLORS, formatCombinationLabel } from "./utils";
import type {
	SubjectCombinationByMajor,
	SubjectCombinationStats,
} from "@/lib/statistics-api.service";

interface SubjectCombinationMatrixProps {
	/** `undefined` = lần tải đầu chưa xong. */
	stats: SubjectCombinationStats | undefined;
}

const TITLE = "Thế mạnh học bạ theo tổ hợp × ngành";

/**
 * Số thí sinh của tổ hợp đông nhất TRONG một ngành, hoặc 0 khi ngành chưa có
 * hồ sơ nào.
 *
 * Trả về giá trị chứ không phải tên tổ hợp: nhiều tổ hợp có thể HOÀ nhau ở
 * đỉnh, và chọn bừa một cái để đánh dấu sẽ nói sai rằng ngành đó có một thế
 * mạnh duy nhất.
 */
export function peakTotalOf(major: Pick<SubjectCombinationByMajor, "combinations">): number {
	return major.combinations.reduce((max, c) => (c.total > max ? c.total : max), 0);
}

/**
 * Ma trận tổ hợp × ngành, kèm bar tổng theo tổ hợp.
 *
 * "Tổ hợp" ở đây là tổ hợp **hệ thống tự chọn để tổng điểm học bạ lớn nhất**,
 * KHÔNG PHẢI tổ hợp thí sinh đăng ký nguyện vọng — số liệu này đo *thế mạnh
 * học bạ* của thí sinh, không đo ý định xét tuyển. Nhãn và mô tả trong
 * component này phải giữ đúng cách gọi đó (xem docblock của
 * `SubjectCombinationStats`).
 */
export function SubjectCombinationMatrix({ stats }: SubjectCombinationMatrixProps) {
	if (stats === undefined) {
		return (
			<ChartCard title={TITLE} filename="the-manh-hoc-ba-to-hop-x-nganh">
				<Skeleton className="h-[360px] w-full" />
			</ChartCard>
		);
	}

	if (stats.rows.length === 0 || stats.byMajor.length === 0) {
		return (
			<ChartCard title={TITLE} filename="the-manh-hoc-ba-to-hop-x-nganh">
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có dữ liệu tổ hợp học bạ trong phạm vi đang lọc.
				</p>
			</ChartCard>
		);
	}

	const totalByCombination = new Map(stats.rows.map((row) => [row.combination, row.total]));
	// Nhãn có mã tổ hợp ("A00 - Toán; Vật lí; Hóa học") dựng MỘT lần rồi tra
	// lại ở cả cột biểu đồ, header bảng và dòng tổng — ba chỗ đó phải gọi tổ
	// hợp giống hệt nhau, nếu không người đọc tưởng là ba thứ khác nhau.
	const labelByCombination = new Map(
		stats.rows.map((row) => [row.combination, formatCombinationLabel(row.combination, row.code)]),
	);
	// Cột sắp A-Z theo NHÃN ("A00 - ..." rồi "B00 - ..."), khác biểu đồ cột phía
	// trên vốn sắp theo số lượng: bảng này rộng và phải cuộn ngang, nên thứ tự
	// dò được bằng mã quan trọng hơn thứ tự xếp hạng — muốn biết tổ hợp nào đông
	// nhất thì đã có sẵn biểu đồ ngay trên.
	const combinations = stats.rows
		.map((row) => row.combination)
		.sort((a, b) =>
			(labelByCombination.get(a) ?? a).localeCompare(labelByCombination.get(b) ?? b, "vi"),
		);
	const maxCellValue = Math.max(
		1,
		...stats.byMajor.flatMap((major) => major.combinations.map((c) => c.total)),
	);

	const barData = stats.rows.map((row) => ({
		combination: formatCombinationLabel(row.combination, row.code),
		"Tổng thí sinh": row.total,
	}));

	return (
		<div className="space-y-4">
			<ChartCard
				title="Tổng thí sinh theo tổ hợp (thế mạnh học bạ)"
				filename="tong-thi-sinh-theo-to-hop"
				description="Tổ hợp hệ thống tự chọn để tổng điểm học bạ lớn nhất — không phải tổ hợp đăng ký nguyện vọng"
			>
				<ResponsiveContainer width="100%" height={chartHeightForRows(barData.length)}>
					<BarChart
						layout="vertical"
						data={barData}
						margin={{ top: 10, right: 48, left: 8, bottom: 10 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis type="number" allowDecimals={false} />
						<YAxis
							type="category"
							dataKey="combination"
							width={CATEGORY_Y_AXIS_WIDTH}
							tick={<WrappedCategoryTickY />}
							interval={0}
						/>
						<Tooltip />
						<Bar
							{...NO_ANIMATION}
							dataKey="Tổng thí sinh"
							fill={COLORS[0]}
							label={BAR_VALUE_LABEL_RIGHT}
						/>
					</BarChart>
				</ResponsiveContainer>
			</ChartCard>

			<ChartCard
				title={TITLE}
				filename="the-manh-hoc-ba-to-hop-x-nganh"
				description="Hàng = ngành, cột = tổ hợp thế mạnh học bạ. Ô đậm hơn = nhiều thí sinh hơn; ô có viền là tổ hợp mạnh nhất của chính ngành đó."
				contentClassName="p-6"
			>
				{/* Bảng có thể rất rộng (nhiều tổ hợp) — cuộn NGANG thay vì bóp cột.
				    Không giới hạn chiều cao: mọi ngành hiện hết trong một mạch, để
				    trang cuộn tự nhiên. Khung cuộn dọc riêng bên trong một trang vốn
				    đã cuộn được là hai thanh cuộn lồng nhau, và bảng dài luôn bị cắt
				    ngang ở giữa dù còn thừa chỗ bên dưới. */}
				<div className="overflow-x-auto rounded border">
					<table className="w-full border-collapse text-xs md:text-sm">
						<thead>
							<tr>
								<th className="sticky left-0 z-20 min-w-40 border-b border-r bg-muted p-2 text-left font-medium">
									Ngành
								</th>
								{combinations.map((combination) => (
									<th
										key={combination}
										className="min-w-28 border-b bg-muted p-2 text-center font-medium"
										title={combination}
									>
										{labelByCombination.get(combination) ?? combination}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{stats.byMajor.map((major) => {
								const cellByCombination = new Map(
									major.combinations.map((c) => [c.combination, c.total]),
								);
								const rowMax = peakTotalOf(major);
								return (
									<tr key={major.majorCode}>
										<td
											className="sticky left-0 z-10 min-w-40 border-r bg-card p-2 font-medium"
											title={major.majorName || major.majorCode}
										>
											{major.majorName || major.majorCode}
										</td>
										{combinations.map((combination) => {
											const value = cellByCombination.get(combination) ?? 0;
											// Nhạt dần theo tỉ trọng trên ô lớn nhất của TOÀN bảng, không
											// phải của riêng hàng này — để đậm/nhạt so sánh được GIỮA các
											// ngành, không chỉ trong nội bộ một ngành.
											const alpha = value === 0 ? 0 : 0.12 + (value / maxCellValue) * 0.78;
											// Thế mạnh của CHÍNH ngành này. Cần viền riêng vì thang màu
											// chuẩn hoá theo ô lớn nhất TOÀN bảng: ngành ít thí sinh có
											// mọi ô nhạt như nhau, không nhìn ra được tổ hợp nào dẫn đầu
											// trong nội bộ ngành đó — mà đó lại là câu hỏi của bảng.
											const isPeak = value > 0 && value === rowMax;
											return (
												<td
													key={combination}
													className={cn(
														"p-2 text-center tabular-nums",
														// Viền + đậm chữ, KHÔNG đổi màu nền: nền đang mang nghĩa
														// "so với toàn bảng", đổi nó sẽ phá mất phép so sánh đó.
														isPeak &&
															"font-semibold outline outline-2 -outline-offset-2 outline-primary",
													)}
													style={{
														backgroundColor:
															value === 0 ? undefined : `hsl(var(--chart-1) / ${alpha})`,
													}}
													title={
														isPeak
															? `Tổ hợp mạnh nhất của ${major.majorName || major.majorCode}`
															: undefined
													}
												>
													{value > 0 ? value.toLocaleString("vi-VN") : ""}
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				<p className="mt-2 text-xs text-muted-foreground">
					Tổng theo tổ hợp:{" "}
					{combinations
						.map(
							(c) =>
								`${labelByCombination.get(c) ?? c} (${(totalByCombination.get(c) ?? 0).toLocaleString("vi-VN")})`,
						)
						.join(" · ")}
				</p>
			</ChartCard>
		</div>
	);
}
